/**
 * Context Menu Configuration
 * 
 * Defines all context menu structures for BIM 2025 style menus.
 */

export interface ContextMenuItem {
    id: string;
    label: string;
    shortcut?: string;
    icon?: string;
    action?: string;
    disabled?: boolean;
    condition?: string; // Condition key for visibility
    /** Submenus may carry dividers — the renderer separates on `type`. */
    submenu?: ContextMenuEntry[];
}

export interface ContextMenuDivider {
    type: 'divider';
}

export type ContextMenuEntry = ContextMenuItem | ContextMenuDivider;

/**
 * Canvas Context Menu (no selection)
 */
export const canvasMenuItems: ContextMenuEntry[] = [
    { id: 'cancel', label: 'Cancel', shortcut: 'Esc', action: 'cancel' },
    { type: 'divider' },
    { id: 'repeat', label: 'Repeat Last Command', shortcut: 'Enter', action: 'repeatLastCommand' },
    {
        id: 'recentCommands',
        label: 'Recent Commands',
        submenu: [
            { id: 'recent1', label: 'Wall', action: 'activateWall' },
            { id: 'recent2', label: 'Door', action: 'activateDoor' },
            { id: 'recent3', label: 'Window', action: 'activateWindow' },
        ]
    },
    { type: 'divider' },
    { id: 'paste', label: 'Paste from Clipboard', shortcut: 'Ctrl+V', action: 'paste' },
    {
        id: 'pasteAligned', label: 'Paste Aligned', submenu: [
            { id: 'pasteAlignedSamePlace', label: 'Same Place', action: 'pasteAlignedSamePlace' },
            { id: 'pasteAlignedSelectedLevels', label: 'Selected Levels', action: 'pasteAlignedSelectedLevels' },
            { id: 'pasteAlignedSelectedViews', label: 'Selected Views', action: 'pasteAlignedSelectedViews' },
        ]
    },
    { type: 'divider' },
    { id: 'selectAll', label: 'Select All', shortcut: 'Ctrl+A', action: 'selectAll' },
    { type: 'divider' },
    { id: 'zoomFit', label: 'Zoom to Fit', shortcut: 'ZF', action: 'zoomToFit' },
    { id: 'zoomRegion', label: 'Zoom In Region', shortcut: 'ZR', action: 'zoomInRegion' },
    { id: 'zoomOut', label: 'Zoom Out (2x)', shortcut: 'ZO', action: 'zoomOut' },
    { id: 'zoomSheet', label: 'Zoom Sheet Size', action: 'zoomSheetSize' },
    { type: 'divider' },
    { id: 'find', label: 'Find/Replace...', shortcut: 'Ctrl+F', action: 'openFind' },
    { type: 'divider' },
    { id: 'viewProperties', label: 'View Properties', action: 'viewProperties' },
    { id: 'browserOrganization', label: 'Browser Organization...', action: 'browserOrganization' },
];

/**
 * Single Element Context Menu
 */
export const singleElementMenuItems: ContextMenuEntry[] = [
    { id: 'cancel', label: 'Cancel', shortcut: 'Esc', action: 'cancel' },
    { type: 'divider' },
    { id: 'repeat', label: 'Repeat Last Command', shortcut: 'Enter', action: 'repeatLastCommand' },
    { type: 'divider' },
    {
        id: 'selectInstances',
        label: 'Select All Instances',
        submenu: [
            { id: 'selectInView', label: 'Visible in View', action: 'selectAllInstancesInView' },
            { id: 'selectInProject', label: 'In Entire Project', action: 'selectAllInstancesInProject' },
        ]
    },
    { type: 'divider' },
    { id: 'delete', label: 'Delete', shortcut: 'Delete', action: 'delete' },
    { type: 'divider' },
    { id: 'move', label: 'Move', shortcut: 'MV', action: 'move' },
    { id: 'copy', label: 'Copy to Clipboard', shortcut: 'Ctrl+C', action: 'copy' },
    { id: 'cut', label: 'Cut to Clipboard', shortcut: 'Ctrl+X', action: 'cut' },
    { id: 'paste', label: 'Paste from Clipboard', shortcut: 'Ctrl+V', action: 'paste' },
    { type: 'divider' },
    {
        id: 'hideInView',
        label: 'Hide in View',
        submenu: [
            { id: 'hideElements', label: 'Elements', shortcut: 'HH', action: 'hideElements' },
            { id: 'hideCategory', label: 'Category', shortcut: 'HC', action: 'hideCategory' },
            { id: 'hideByFilter', label: 'By Filter...', action: 'hideByFilter' },
        ]
    },
    {
        id: 'isolate',
        label: 'Isolate',
        submenu: [
            { id: 'isolateElement', label: 'Element', shortcut: 'HI', action: 'isolateElement' },
            { id: 'isolateCategory', label: 'Category', shortcut: 'IC', action: 'isolateCategory' },
        ]
    },
    {
        id: 'override',
        label: 'Override Graphics in View',
        submenu: [
            { id: 'overrideByElement', label: 'By Element...', action: 'overrideByElement' },
            { id: 'overrideByCategory', label: 'By Category...', action: 'overrideByCategory' },
            { id: 'overrideByFilter', label: 'By Filter...', action: 'overrideByFilter' },
        ]
    },
    { type: 'divider' },
    { id: 'createSimilar', label: 'Create Similar', shortcut: 'CS', action: 'createSimilar' },
    { id: 'editFamily', label: 'Edit Family', action: 'editFamily', condition: 'hasFamily' },
    { id: 'editType', label: 'Edit Type...', action: 'editType' },
    { type: 'divider' },
    { id: 'flipFacing', label: 'Flip Facing', action: 'flipFacing', condition: 'canFlip' },
    { id: 'flipHand', label: 'Flip Hand', action: 'flipHand', condition: 'canFlip' },
    { id: 'rotate', label: 'Rotate', shortcut: 'RO', action: 'rotate' },
    {
        id: 'mirror', label: 'Mirror', submenu: [
            { id: 'mirrorPickAxis', label: 'Pick Axis', action: 'mirrorPickAxis' },
            { id: 'mirrorDrawAxis', label: 'Draw Axis', action: 'mirrorDrawAxis' },
        ]
    },
    { type: 'divider' },
    { id: 'pin', label: 'Pin', action: 'pin' },
    { id: 'unpin', label: 'Unpin', action: 'unpin' },
    { type: 'divider' },
    { id: 'reloadLinkedCad', label: 'Reload', action: 'reloadLinkedCad', condition: 'isLinkedCad' },
    { type: 'divider' },
    { id: 'makeWorkPlane', label: 'Make Work Plane', action: 'makeWorkPlane', condition: 'isSurface' },
    { type: 'divider' },
    { id: 'zoomFit', label: 'Zoom to Fit', shortcut: 'ZF', action: 'zoomToFit' },
    { id: 'zoomSelection', label: 'Zoom to Selection', shortcut: 'ZS', action: 'zoomToSelection' },
    { type: 'divider' },
    { id: 'properties', label: 'Properties', action: 'showProperties' },
];

/**
 * Multi-Selection Context Menu
 */
export const multiSelectionMenuItems: ContextMenuEntry[] = [
    { id: 'cancel', label: 'Cancel', shortcut: 'Esc', action: 'cancel' },
    { type: 'divider' },
    { id: 'repeat', label: 'Repeat Last Command', shortcut: 'Enter', action: 'repeatLastCommand' },
    { type: 'divider' },
    { id: 'filterSelection', label: 'Filter...', action: 'filterSelection' },
    { type: 'divider' },
    { id: 'delete', label: 'Delete', shortcut: 'Delete', action: 'delete' },
    { type: 'divider' },
    { id: 'move', label: 'Move', shortcut: 'MV', action: 'move' },
    { id: 'copy', label: 'Copy to Clipboard', shortcut: 'Ctrl+C', action: 'copy' },
    { id: 'cut', label: 'Cut to Clipboard', shortcut: 'Ctrl+X', action: 'cut' },
    { id: 'paste', label: 'Paste from Clipboard', shortcut: 'Ctrl+V', action: 'paste' },
    { type: 'divider' },
    {
        id: 'hideInView',
        label: 'Hide in View',
        submenu: [
            { id: 'hideElements', label: 'Elements', shortcut: 'HH', action: 'hideElements' },
            { id: 'hideCategory', label: 'Category', shortcut: 'HC', action: 'hideCategory' },
        ]
    },
    {
        id: 'isolate',
        label: 'Isolate',
        submenu: [
            { id: 'isolateElement', label: 'Elements', shortcut: 'HI', action: 'isolateElement' },
            { id: 'isolateCategory', label: 'Category', shortcut: 'IC', action: 'isolateCategory' },
        ]
    },
    { type: 'divider' },
    { id: 'group', label: 'Create Group', shortcut: 'GP', action: 'createGroup' },
    { type: 'divider' },
    { id: 'pin', label: 'Pin', action: 'pin' },
    { id: 'unpin', label: 'Unpin', action: 'unpin' },
    { type: 'divider' },
    { id: 'zoomFit', label: 'Zoom to Fit', shortcut: 'ZF', action: 'zoomToFit' },
    { id: 'zoomSelection', label: 'Zoom to Selection', shortcut: 'ZS', action: 'zoomToSelection' },
    { type: 'divider' },
    { id: 'properties', label: 'Properties', action: 'showProperties' },
];

/**
 * Wall Tool Active Context Menu
 */
export const wallToolMenuItems: ContextMenuEntry[] = [
    { id: 'cancel', label: 'Cancel', shortcut: 'Esc', action: 'cancel' },
    { type: 'divider' },
    { id: 'finishWall', label: 'Finish Wall', action: 'finishWall' },
    { id: 'restartWall', label: 'Restart Wall', action: 'restartWall' },
    { type: 'divider' },
    { id: 'flipOrientation', label: 'Flip Orientation', action: 'flipOrientation' },
    { type: 'divider' },
    {
        id: 'locationLine',
        label: 'Wall Location Line',
        submenu: [
            { id: 'wallCenterline', label: 'Wall Centerline', action: 'setLocationWallCenterline' },
            { id: 'coreCenterline', label: 'Core Centerline', action: 'setLocationCoreCenterline' },
            { id: 'finishFaceInterior', label: 'Finish Face: Interior', action: 'setLocationFinishFaceInterior' },
            { id: 'finishFaceExterior', label: 'Finish Face: Exterior', action: 'setLocationFinishFaceExterior' },
            { id: 'coreFaceInterior', label: 'Core Face: Interior', action: 'setLocationCoreFaceInterior' },
            { id: 'coreFaceExterior', label: 'Core Face: Exterior', action: 'setLocationCoreFaceExterior' },
        ]
    },
    { type: 'divider' },
    { id: 'chain', label: 'Chain', action: 'toggleChain' },
    { id: 'offset', label: 'Offset...', action: 'setOffset' },
    { id: 'radius', label: 'Radius...', action: 'setRadius' },
];

/**
 * ViewCube Context Menu
 */
export const viewCubeMenuItems: ContextMenuEntry[] = [
    { id: 'goHome', label: 'Go Home', action: 'viewCubeGoHome' },
    { type: 'divider' },
    { id: 'toggleProjection', label: 'Toggle Perspective/Orthographic', action: 'viewCubeToggleProjection' },
    { type: 'divider' },
    { id: 'saveViewAsHome', label: 'Save View as Home', action: 'viewCubeSaveViewAsHome' },
    { id: 'lockToSelection', label: 'Lock to Selection', action: 'viewCubeLockToSelection' },
    { type: 'divider' },
    { id: 'options', label: 'Options...', action: 'viewCubeOptions' },
];

/**
 * Get menu items based on context
 */
export function getContextMenuItems(
    selectedIds: string[],
    activeTool: string | null,
    targetElementId: string | null
): ContextMenuEntry[] {
    // ViewCube Context
    if (targetElementId === 'viewcube') {
        return viewCubeMenuItems;
    }

    // If a tool is active, return tool-specific menu
    if (activeTool === 'WALL') {
        return wallToolMenuItems;
    }

    // If multiple elements selected
    if (selectedIds.length > 1) {
        return multiSelectionMenuItems;
    }

    // If single element selected
    if (selectedIds.length === 1) {
        return singleElementMenuItems;
    }

    // No selection - show canvas menu
    return canvasMenuItems;
}

